import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LogOut, Download, Users, User, Heart, Search, Trash2, Key, Eye, EyeOff, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUN_TYPE_LABELS = {
  erwachsenenlauf: "Erwachsenenlauf",
  kinderlauf: "Kinderlauf",
  bambini: "Bambini-Lauf",
  helfer: "Helfer:in",
  erwachsene: "Erwachsenenlauf",
  kinder: "Kinderlauf",
};

const MONAT_NAMES = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatGeb(tag, monat, jahr) {
  if (!tag && !monat && !jahr) return "-";
  const m = monat ? MONAT_NAMES[parseInt(monat)] || monat : "";
  return `${tag || "?"}.${m || "?"}.${jahr || "?"}`;
}

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [storedPw, setStoredPw] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companyRegs, setCompanyRegs] = useState([]);
  const [companyStats, setCompanyStats] = useState({ total_companies: 0, total_participants: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [tab, setTab] = useState("registrations");
  const [pwDialog, setPwDialog] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [newPwConfirm, setNewPwConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setAuthed(true);
      setStoredPw(password);
      toast.success("Angemeldet!");
    } catch {
      toast.error("Falsches Passwort.");
    }
  };

  const loadData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/registrations?password=${encodeURIComponent(storedPw)}`),
      axios.get(`${API}/admin/contacts?password=${encodeURIComponent(storedPw)}`),
      axios.get(`${API}/admin/company-registrations?password=${encodeURIComponent(storedPw)}`),
      axios.get(`${API}/admin/company-registrations/stats?password=${encodeURIComponent(storedPw)}`),
    ])
      .then(([regRes, conRes, compRes, compStatsRes]) => {
        setRegistrations(regRes.data || []);
        setContacts(conRes.data || []);
        setCompanyRegs(compRes.data || []);
        setCompanyStats(compStatsRes.data || { total_companies: 0, total_participants: 0 });
      })
      .catch(() => toast.error("Fehler beim Laden."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authed) loadData();
  }, [authed]);

  const stats = useMemo(() => {
    const total = registrations.length;
    const adults = registrations.filter((r) => r.run_type === "erwachsenenlauf" || r.run_type === "erwachsene").length;
    const kids = registrations.filter((r) => r.run_type === "kinderlauf" || r.run_type === "kinder" || r.run_type === "bambini").length;
    const helpers = registrations.filter((r) => r.run_type === "helfer").length;
    return { total, adults, kids, helpers };
  }, [registrations]);

  const filtered = useMemo(() => {
    if (!search) return registrations;
    const q = search.toLowerCase();
    return registrations.filter(
      (r) =>
        (r.vorname || r.name || "").toLowerCase().includes(q) ||
        (r.nachname || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (RUN_TYPE_LABELS[r.run_type] || r.run_type || "").toLowerCase().includes(q)
    );
  }, [registrations, search]);

  const handleDelete = async (id, type) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      const endpoint = type === "reg" ? "registrations" : "contacts";
      await axios.delete(`${API}/admin/${endpoint}/${id}?password=${encodeURIComponent(storedPw)}`);
      toast.success("Gelöscht!");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      loadData();
    } catch {
      toast.error("Fehler beim Löschen.");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error("Keine Einträge ausgewählt.");
      return;
    }
    if (!window.confirm(`Wirklich ${selectedIds.size} Einträge löschen?`)) return;
    try {
      const deletePromises = Array.from(selectedIds).map((id) =>
        axios.delete(`${API}/admin/registrations/${id}?password=${encodeURIComponent(storedPw)}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} Einträge gelöscht!`);
      setSelectedIds(new Set());
      loadData();
    } catch {
      toast.error("Fehler beim Löschen einiger Einträge.");
      loadData();
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== newPwConfirm) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Mindestens 8 Zeichen erforderlich.");
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: storedPw, new_password: newPw });
      setStoredPw(newPw);
      setPwDialog(false);
      setNewPw("");
      setNewPwConfirm("");
      toast.success("Passwort geändert!");
    } catch {
      toast.error("Fehler beim Ändern des Passworts.");
    }
  };

  const exportCSV = () => {
    const headers = ["Vorname", "Nachname", "E-Mail", "Kategorie", "Geburtsdatum", "Datenschutz", "Haftung", "Angemeldet am"];
    const rows = registrations.map((r) => [
      r.vorname || r.name || "",
      r.nachname || "",
      r.email || "",
      RUN_TYPE_LABELS[r.run_type] || r.run_type || "",
      formatGeb(r.geburtsdatum_tag, r.geburtsdatum_monat, r.geburtsdatum_jahr),
      r.datenschutz_akzeptiert ? "Ja" : "Nein",
      r.haftungsausschluss_akzeptiert ? "Ja" : "Nein",
      formatDate(r.created_at),
    ]);
    const csv = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `anmeldungen_runforrett_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportiert!");
  };

  // Flatten company registrations for table display
  const flattenedCompanyRegs = useMemo(() => {
    const flat = [];
    companyRegs.forEach((reg) => {
      (reg.teilnehmer || []).forEach((t, idx) => {
        flat.push({
          _companyId: reg.id,
          _companyName: reg.firmenname,
          _rechnungsadresse: reg.rechnungsadresse || "",
          _rechnungs_email: reg.rechnungs_email || "",
          _ansprechpartner: reg.ansprechpartner_buchhaltung || "",
          _created_at: reg.created_at,
          _participantIndex: idx,
          ...t,
        });
      });
    });
    return flat;
  }, [companyRegs]);

  const filteredCompanyRegs = useMemo(() => {
    if (!companySearch) return flattenedCompanyRegs;
    const q = companySearch.toLowerCase();
    return flattenedCompanyRegs.filter(
      (r) =>
        (r._companyName || "").toLowerCase().includes(q) ||
        (r.vorname || "").toLowerCase().includes(q) ||
        (r.nachname || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q)
    );
  }, [flattenedCompanyRegs, companySearch]);

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Wirklich diese Firmenanmeldung löschen? Alle Teilnehmer werden entfernt.")) return;
    try {
      await axios.delete(`${API}/admin/company-registrations/${companyId}?password=${encodeURIComponent(storedPw)}`);
      toast.success("Firmenanmeldung gelöscht!");
      loadData();
    } catch {
      toast.error("Fehler beim Löschen.");
    }
  };

  const handleDeleteCompanyParticipant = async (companyId, participantIndex, participantName) => {
    if (!window.confirm(`Wirklich "${participantName}" aus der Firmenanmeldung löschen?`)) return;
    try {
      const res = await axios.delete(`${API}/admin/company-registrations/${companyId}/participant/${participantIndex}?password=${encodeURIComponent(storedPw)}`);
      if (res.data.company_deleted) {
        toast.success("Letzter Teilnehmer gelöscht. Firmenanmeldung wurde entfernt.");
      } else {
        toast.success("Teilnehmer gelöscht!");
      }
      loadData();
    } catch {
      toast.error("Fehler beim Löschen.");
    }
  };

  const exportCompanyCSV = () => {
    const headers = ["Firma", "Rechnungsadresse", "Rechnungs-E-Mail", "Ansprechpartner", "Vorname", "Nachname", "E-Mail", "Geburtsdatum", "Angemeldet am"];
    const rows = flattenedCompanyRegs.map((r) => [
      r._companyName || "",
      r._rechnungsadresse || "",
      r._rechnungs_email || "",
      r._ansprechpartner || "",
      r.vorname || "",
      r.nachname || "",
      r.email || "",
      formatGeb(r.geburtsdatum_tag, r.geburtsdatum_monat, r.geburtsdatum_jahr),
      formatDate(r._created_at),
    ]);
    const csv = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `firmenanmeldungen_runforrett_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportiert!");
  };

  // --- LOGIN ---
  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f6eef7 0%, #e8f7f0 100%)" }}>
        <Card className="w-full max-w-sm border-0 shadow-xl overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
                <Lock className="w-7 h-7" style={{ color: "#b46eb6" }} />
              </div>
              <h1 className="font-heading text-2xl font-bold" style={{ color: "#1F2937" }}>Admin-Bereich</h1>
              <p className="text-sm mt-1" style={{ color: "#9CA3AF" }}>Run for Rett Verwaltung</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-heading font-semibold text-sm" style={{ color: "#1F2937" }}>Passwort</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin-Passwort eingeben"
                  className="rounded-lg border-gray-200 bg-gray-50 h-11"
                  required
                />
              </div>
              <Button data-testid="admin-login-btn" type="submit" className="w-full rounded-full py-5 font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white">
                Anmelden
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm hover:underline" style={{ color: "#b46eb6" }}>Zurück zur Website</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- DASHBOARD ---
  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ background: "#F8F9FA" }}>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo-runforrett.svg" alt="Logo" className="h-8 w-auto" />
            <span className="font-heading font-bold text-base" style={{ color: "#1F2937" }}>Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" size="sm" className="text-sm text-gray-500">Website</Button></Link>
            <Button data-testid="admin-change-pw-btn" variant="ghost" size="sm" onClick={() => setPwDialog(true)} className="text-sm text-gray-500">
              <Key className="w-4 h-4 mr-1" /> Passwort
            </Button>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={() => { setAuthed(false); setStoredPw(""); setPassword(""); }} className="text-sm text-gray-500">
              <LogOut className="w-4 h-4 mr-1" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180,110,182,0.15)" }}>
                <Users className="w-5 h-5" style={{ color: "#b46eb6" }} />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold" style={{ color: "#b46eb6" }}>{stats.total}</p>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>Gesamt</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180,110,182,0.15)" }}>
                <User className="w-5 h-5" style={{ color: "#b46eb6" }} />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold" style={{ color: "#b46eb6" }}>{stats.adults}</p>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>Erwachsene</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(149,225,187,0.25)" }}>
                <User className="w-5 h-5" style={{ color: "#7DD4B5" }} />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold" style={{ color: "#7DD4B5" }}>{stats.kids}</p>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>Kinder</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(149,225,187,0.25)" }}>
                <Heart className="w-5 h-5" style={{ color: "#7DD4B5" }} />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold" style={{ color: "#7DD4B5" }}>{stats.helpers}</p>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>Helfer:innen</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Button data-testid="tab-registrations" variant={tab === "registrations" ? "default" : "outline"} onClick={() => setTab("registrations")}
            className={`rounded-full px-5 text-sm font-semibold ${tab === "registrations" ? "bg-[#b46eb6] hover:bg-[#8f5691] text-white" : "text-gray-500"}`}>
            Anmeldungen ({registrations.length})
          </Button>
          <Button data-testid="tab-company" variant={tab === "company" ? "default" : "outline"} onClick={() => setTab("company")}
            className={`rounded-full px-5 text-sm font-semibold ${tab === "company" ? "bg-[#b46eb6] hover:bg-[#8f5691] text-white" : "text-gray-500"}`}>
            <Building2 className="w-4 h-4 mr-1" /> Firmenanmeldungen ({companyStats.total_companies})
          </Button>
          <Button data-testid="tab-contacts" variant={tab === "contacts" ? "default" : "outline"} onClick={() => setTab("contacts")}
            className={`rounded-full px-5 text-sm font-semibold ${tab === "contacts" ? "bg-[#b46eb6] hover:bg-[#8f5691] text-white" : "text-gray-500"}`}>
            Kontaktnachrichten ({contacts.length})
          </Button>
        </div>

        {tab === "registrations" && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#9CA3AF" }} />
                <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suchen (Name, E-Mail, Kategorie...)" className="pl-10 rounded-lg border-gray-200 bg-white h-10" />
              </div>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <Button data-testid="admin-delete-selected" onClick={handleDeleteSelected} variant="outline" 
                    className="rounded-full px-5 text-sm font-semibold border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> {selectedIds.size} löschen
                  </Button>
                )}
                <Button data-testid="admin-export-csv" onClick={exportCSV} variant="outline" className="rounded-full px-5 text-sm font-semibold border-[#b46eb6]/20 text-[#b46eb6]">
                  <Download className="w-4 h-4 mr-2" /> CSV exportieren
                </Button>
              </div>
            </div>

            <Card className="border-0 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid="admin-registrations-table" className="w-full text-left">
                  <thead>
                    <tr className="border-b" style={{ background: "rgba(180,110,182,0.04)" }}>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>
                        <Checkbox
                          data-testid="select-all-checkbox"
                          checked={filtered.length > 0 && selectedIds.size === filtered.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>#</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Kategorie</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Vorname</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Nachname</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>E-Mail</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Geburtsdatum</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Angemeldet</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: "#9CA3AF" }}>Lädt...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: "#9CA3AF" }}>Keine Anmeldungen gefunden.</td></tr>
                    ) : (
                      filtered.map((r, i) => (
                        <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${selectedIds.has(r.id) ? "bg-purple-50/50" : ""}`}>
                          <td className="px-4 py-3">
                            <Checkbox
                              data-testid={`select-reg-${r.id}`}
                              checked={selectedIds.has(r.id)}
                              onCheckedChange={() => toggleSelect(r.id)}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#9CA3AF" }}>{filtered.length - i}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{
                                background: r.run_type === "kinderlauf" || r.run_type === "kinder" || r.run_type === "bambini" ? "rgba(149,225,187,0.15)" : "rgba(180,110,182,0.12)",
                                color: r.run_type === "kinderlauf" || r.run_type === "kinder" || r.run_type === "bambini" ? "#059669" : "#b46eb6",
                              }}>
                              {RUN_TYPE_LABELS[r.run_type] || r.run_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium" style={{ color: "#1F2937" }}>{r.vorname || r.name || "-"}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{r.nachname || "-"}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{r.email}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{formatGeb(r.geburtsdatum_tag, r.geburtsdatum_monat, r.geburtsdatum_jahr)}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: "#9CA3AF" }}>{formatDate(r.created_at)}</td>
                          <td className="px-4 py-3">
                            <Button data-testid={`delete-reg-${r.id}`} variant="ghost" size="sm" onClick={() => handleDelete(r.id, "reg")}
                              className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {tab === "contacts" && (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table data-testid="admin-contacts-table" className="w-full text-left">
                <thead>
                  <tr className="border-b" style={{ background: "rgba(180,110,182,0.04)" }}>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>#</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Name</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>E-Mail</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Betreff</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Nachricht</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Datum</th>
                    <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm" style={{ color: "#9CA3AF" }}>Keine Nachrichten vorhanden.</td></tr>
                  ) : (
                    contacts.map((c, i) => (
                      <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-sm" style={{ color: "#9CA3AF" }}>{i + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium" style={{ color: "#1F2937" }}>{c.name}</td>
                        <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{c.email}</td>
                        <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{c.subject || "-"}</td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate" style={{ color: "#4B5563" }}>{c.message}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: "#9CA3AF" }}>{formatDate(c.created_at)}</td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id, "contact")}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* FIRMENANMELDUNGEN TAB */}
        {tab === "company" && (
          <>
            {/* Company Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180,110,182,0.15)" }}>
                    <Building2 className="w-5 h-5" style={{ color: "#b46eb6" }} />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold" style={{ color: "#b46eb6" }}>{companyStats.total_companies}</p>
                    <p className="text-xs" style={{ color: "#9CA3AF" }}>Firmen</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(149,225,187,0.25)" }}>
                    <Users className="w-5 h-5" style={{ color: "#7DD4B5" }} />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold" style={{ color: "#7DD4B5" }}>{companyStats.total_participants}</p>
                    <p className="text-xs" style={{ color: "#9CA3AF" }}>Teilnehmer</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#9CA3AF" }} />
                <Input data-testid="company-search" value={companySearch} onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="Suchen (Firma, Name, E-Mail...)" className="pl-10 rounded-lg border-gray-200 bg-white h-10" />
              </div>
              <Button data-testid="company-export-csv" onClick={exportCompanyCSV} variant="outline" className="rounded-full px-5 text-sm font-semibold border-[#b46eb6]/20 text-[#b46eb6]">
                <Download className="w-4 h-4 mr-2" /> CSV exportieren
              </Button>
            </div>

            <Card className="border-0 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid="admin-company-table" className="w-full text-left">
                  <thead>
                    <tr className="border-b" style={{ background: "rgba(180,110,182,0.04)" }}>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Firma</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Vorname</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Nachname</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>E-Mail</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Geburtsdatum</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Angemeldet</th>
                      <th className="px-4 py-3 text-xs font-heading font-bold uppercase tracking-wider" style={{ color: "#9CA3AF" }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-sm" style={{ color: "#9CA3AF" }}>Lädt...</td></tr>
                    ) : filteredCompanyRegs.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-sm" style={{ color: "#9CA3AF" }}>Keine Firmenanmeldungen gefunden.</td></tr>
                    ) : (
                      filteredCompanyRegs.map((r, i) => (
                        <tr key={`${r._companyId}-${r._participantIndex}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium" style={{ color: "#1F2937" }}>{r._companyName}</div>
                            {r._participantIndex === 0 && r._rechnungs_email && (
                              <div className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>{r._rechnungs_email}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{r.vorname || "-"}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{r.nachname || "-"}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{r.email || "-"}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: "#4B5563" }}>{formatGeb(r.geburtsdatum_tag, r.geburtsdatum_monat, r.geburtsdatum_jahr)}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: "#9CA3AF" }}>{formatDate(r._created_at)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button 
                                data-testid={`delete-participant-${r._companyId}-${r._participantIndex}`} 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteCompanyParticipant(r._companyId, r._participantIndex, `${r.vorname} ${r.nachname}`)}
                                className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0" 
                                title="Teilnehmer löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Passwort ändern Dialog */}
      <Dialog open={pwDialog} onOpenChange={setPwDialog}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="change-pw-dialog">
          <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#1F2937" }}>Passwort ändern</DialogTitle>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-sm font-semibold" style={{ color: "#1F2937" }}>Neues Passwort</Label>
              <div className="relative">
                <Input data-testid="new-pw-input" type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Neues Passwort (min. 8 Zeichen)" className="rounded-lg border-gray-200 bg-gray-50 h-11 pr-10" required minLength={8} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold" style={{ color: "#1F2937" }}>Passwort bestätigen</Label>
              <Input data-testid="confirm-pw-input" type="password" value={newPwConfirm} onChange={(e) => setNewPwConfirm(e.target.value)}
                placeholder="Passwort wiederholen" className="rounded-lg border-gray-200 bg-gray-50 h-11" required />
            </div>
            <Button data-testid="save-pw-btn" type="submit" className="w-full rounded-full py-5 font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white">
              Passwort speichern
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
